import {
  ArrowSquareOutIcon,
  CalculatorIcon,
  ChartBarIcon,
  DatabaseIcon,
  InfoIcon,
  NotePencilIcon,
  StorefrontIcon,
} from '@phosphor-icons/react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@shared/ui/Modal/Modal';
import { Badge, Skeleton, StateBlock } from '@shared/ui';

import { useVehicleProvenance } from './api';
import { formatObservedDate } from './lib';
import type { ProvenanceEntryData, ProvenanceSourceType, VehicleProvenanceData } from './types';
import styles from './ProvenanceInfo.module.css';

interface ProvenanceInfoProps {
  /** UUID автомобиля: endpoint происхождения принимает id, а не slug. */
  vehicleId: string;
  /**
   * Поля, на которых построен блок рядом с кнопкой. Записи по ним показываются
   * первыми; остальные значения карточки остаются доступны ниже.
   */
  fields?: readonly string[] | undefined;
  /** Что именно объясняет модалка — попадает в заголовок и в aria-label кнопки. */
  subject: string;
  /** Ссылка на исходное объявление, если источник её разрешает (MVP_PRD §17). */
  source?: { name: string; url: string | null } | undefined;
}

const SOURCE_ICONS: Record<ProvenanceSourceType, ReactNode> = {
  seller_listing: <StorefrontIcon size={16} aria-hidden="true" />,
  catalog_import: <DatabaseIcon size={16} aria-hidden="true" />,
  market_benchmark: <ChartBarIcon size={16} aria-hidden="true" />,
  carviq_calculation: <CalculatorIcon size={16} aria-hidden="true" />,
  editorial_assessment: <NotePencilIcon size={16} aria-hidden="true" />,
};

/**
 * Кнопка информации и модалка происхождения данных (FE-062, MVP_PRD §17).
 *
 * Стоит рядом с каждым аналитическим блоком и отвечает на один вопрос: откуда
 * взялось то, что показано выше. По каждому значению — откуда оно (объявление
 * продавца, импорт каталога, рыночная выборка, расчёт CarViQ.DE, редакционная
 * оценка), когда получено у источника, когда зафиксировано, и ссылка на
 * объявление, если источник её разрешает.
 *
 * **Служебные подробности сюда не выводятся.** Формула расчёта, версия
 * алгоритма, идентификатор рыночной выборки и сырые значения источника приходят
 * в ответе BE-064, но остаются в API: пользователю они ничего не объясняют, а
 * читаются как отладочная информация. Продуктовое решение — показывать только
 * происхождение, не внутреннюю кухню.
 *
 * Данные грузятся при открытии, а не вместе со страницей: модалку открывает
 * меньшинство посетителей. Пока запрос идёт — скелетоны; ошибка и пустой ответ
 * различаются текстом, потому что «не удалось загрузить» и «происхождение не
 * записано» — разные ситуации, и вторую нельзя выдавать за первую.
 */
export function ProvenanceInfo({ vehicleId, fields, subject, source }: ProvenanceInfoProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const titleId = useId();

  const provenance = useVehicleProvenance(vehicleId, open);
  const locale = i18n.resolvedLanguage ?? 'de';

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => {
          setOpen(true);
        }}
        aria-label={t('vehicle.provenance.open', { subject })}
      >
        <InfoIcon size={16} weight="bold" aria-hidden="true" />
        <span aria-hidden="true">{t('vehicle.provenance.short')}</span>
      </button>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        labelledBy={titleId}
        className={styles.dialog}
      >
        {/* Содержимое монтируется только у открытой модалки: закрытый диалог не
            должен держать в дереве доступности вторую копию всех записей. */}
        {open ? (
          <div className={styles.body}>
            <header className={styles.header}>
              <h2 id={titleId} className={styles.title}>
                {t('vehicle.provenance.title')}
              </h2>
              <p className={styles.lead}>{t('vehicle.provenance.lead', { subject })}</p>
            </header>

            <ProvenanceBody
              isPending={provenance.isPending}
              isError={provenance.isError}
              data={provenance.data}
              fields={fields}
              locale={locale}
            />

            {source?.url ? (
              <a
                className={styles.sourceLink}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {t('vehicle.sourceLink', { name: source.name })}
                <ArrowSquareOutIcon size={16} aria-hidden="true" />
              </a>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
}

interface ProvenanceBodyProps {
  isPending: boolean;
  isError: boolean;
  data: VehicleProvenanceData | undefined;
  fields: readonly string[] | undefined;
  locale: string;
}

function ProvenanceBody({ isPending, isError, data, fields, locale }: ProvenanceBodyProps) {
  const { t } = useTranslation();

  if (isPending) {
    return (
      <div className={styles.loading}>
        <p className="visually-hidden" role="status">
          {t('vehicle.provenance.loading')}
        </p>
        <Skeleton height="1.25rem" width="45%" />
        <Skeleton height="4.5rem" radius="var(--radius-card)" />
        <Skeleton height="4.5rem" radius="var(--radius-card)" />
      </div>
    );
  }

  if (isError || data === undefined) {
    return (
      <StateBlock
        tone="error"
        compact
        title={t('vehicle.provenance.error.title')}
        description={t('vehicle.provenance.error.text')}
      />
    );
  }

  if (data.entries.length === 0) {
    return (
      <StateBlock
        tone="neutral"
        compact
        title={t('vehicle.provenance.empty.title')}
        description={t('vehicle.provenance.empty.text')}
      />
    );
  }

  const selected = fields ?? [];
  const primary = data.entries.filter((entry) => selected.includes(entry.field_name));
  const rest = data.entries.filter((entry) => !selected.includes(entry.field_name));

  return (
    <>
      {primary.length > 0 ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('vehicle.provenance.sectionBlock')}</h3>
          <ul className={styles.entries}>
            {primary.map((entry) => (
              <li key={entry.field_name}>
                <ProvenanceEntry entry={entry} locale={locale} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Записи именно этого блока не нашлось: показываем это прямо, а не
          подставляем вместо неё соседние значения. */}
      {selected.length > 0 && primary.length === 0 ? (
        <StateBlock
          tone="neutral"
          compact
          title={t('vehicle.provenance.missingField.title')}
          description={t('vehicle.provenance.missingField.text')}
        />
      ) : null}

      {rest.length > 0 ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {selected.length > 0
              ? t('vehicle.provenance.sectionOther')
              : t('vehicle.provenance.sectionAll')}
          </h3>
          <ul className={styles.entries}>
            {rest.map((entry) => (
              <li key={entry.field_name}>
                <ProvenanceEntry entry={entry} locale={locale} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function ProvenanceEntry({ entry, locale }: { entry: ProvenanceEntryData; locale: string }) {
  const { t } = useTranslation();
  const noData = t('catalog.noData');

  const meta: { key: string; value: string }[] = [
    { key: 'observedAt', value: formatObservedDate(entry.observed_at, locale) ?? noData },
    { key: 'recordedAt', value: formatObservedDate(entry.recorded_at, locale) ?? noData },
  ];

  return (
    <article className={styles.entry}>
      <header className={styles.entryHead}>
        <h4 className={styles.entryTitle}>
          {t(`vehicle.provenance.field.${entry.field_name}`, { defaultValue: entry.field_name })}
        </h4>
        <Badge tone="info" icon={SOURCE_ICONS[entry.source_type]}>
          {t(`vehicle.provenance.source.${entry.source_type}`, {
            defaultValue: entry.source_type,
          })}
        </Badge>
      </header>

      <dl className={styles.meta}>
        {meta.map((row) => (
          <div key={row.key}>
            <dt>{t(`vehicle.provenance.${row.key}`)}</dt>
            <dd className="tabular">{row.value}</dd>
          </div>
        ))}
      </dl>

      {entry.source?.url ? (
        <a
          className={styles.entryLink}
          href={entry.source.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {t('vehicle.sourceLink', { name: entry.source.name })}
          <ArrowSquareOutIcon size={14} aria-hidden="true" />
        </a>
      ) : null}
    </article>
  );
}
